import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaExternalLinkAlt, FaLock, FaShieldAlt } from 'react-icons/fa';
import { AuthContext, BASE_URL } from '../AuthContext';
import LoadingSpinner from './LoadingSpinner';
import NavigationBar from '@/components/NavigationBar';
import { notificationStyles } from '../utils';
import PublicDisclosureSettings from './PublicDisclosureSettings';
import { TERMS_VERSION, PRIVACY_VERSION } from '../policyVersions';

const AccountSettings = () => {
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTwoFAPrompt, setShowTwoFAPrompt] = useState(false);
  const [oauthStatus, setOauthStatus] = useState({ configured: false, connected: false });
  const [connectionStatusKnown, setConnectionStatusKnown] = useState(false);
  const [connectionRefresh, setConnectionRefresh] = useState(0);
  const [connectionFeedback, setConnectionFeedback] = useState(null);
  const connectionFeedbackRef = useRef(null);
  const [profile, setProfile] = useState({ username: '', email: '', country: '', phone_number: '', lifecycle_state: '' });
  const [briefing, setBriefing] = useState({ enabled: false, delivery_time: '08:00', timezone: 'Europe/Malta', last_delivery: null });
  const [tradeAlerts, setTradeAlerts] = useState({ entry_alerts_enabled: false, exit_alerts_enabled: false });
  const [isSavingBriefing, setIsSavingBriefing] = useState(false);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [isSavingTradeAlerts, setIsSavingTradeAlerts] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState(null);
  const [briefingFeedback, setBriefingFeedback] = useState(null);
  const [testEmailFeedback, setTestEmailFeedback] = useState(null);
  const [tradeAlertFeedback, setTradeAlertFeedback] = useState(null);
  const [practiceConsent, setPracticeConsent] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentFeedback, setConsentFeedback] = useState(null);
  const briefingSaveButtonRef = useRef(null);
  const testEmailButtonRef = useRef(null);
  const tradeAlertSaveButtonRef = useRef(null);
  const profileSaveButtonRef = useRef(null);
  const profileSaveInFlight = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, isTokenVerified, verifyAndRefreshToken } = useContext(AuthContext);

  useEffect(() => {
    if (!connectionFeedback) return;
    connectionFeedbackRef.current?.focus();
    connectionFeedbackRef.current?.scrollIntoView({ block: 'nearest' });
  }, [connectionFeedback]);

  useEffect(() => {
    if (!isSavingBriefing && briefingFeedback) briefingSaveButtonRef.current?.focus();
  }, [isSavingBriefing, briefingFeedback]);

  useEffect(() => {
    if (!isSendingTestEmail && testEmailFeedback) testEmailButtonRef.current?.focus();
  }, [isSendingTestEmail, testEmailFeedback]);

  useEffect(() => {
    if (!isSavingTradeAlerts && tradeAlertFeedback) tradeAlertSaveButtonRef.current?.focus();
  }, [isSavingTradeAlerts, tradeAlertFeedback]);

  useEffect(() => {
    if (!isSavingProfile && profileFeedback) profileSaveButtonRef.current?.focus();
  }, [isSavingProfile, profileFeedback]);

  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!['notifications', 'morning-briefing', 'trade-suggestion-alerts', 'public-disclosure-alerts', 'practice-consent'].includes(id)) return;
    const target = document.getElementById(id);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target?.focus();
  }, [location.hash]);

  const focusNotificationSection = (id) => {
    window.history.replaceState(null, '', window.location.pathname + '#' + id);
    const target = document.getElementById(id);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target?.focus();
  };

  useEffect(() => {
    if (!token || !isTokenVerified) return;
    axios.get(`${BASE_URL}/api/account/practice-consent`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => setPracticeConsent(response.data)).catch(() => {
      setNotification({ type: 'error', message: 'Practice policy status is unavailable. Please retry later.' });
    });
  }, [token, isTokenVerified]);

  const policyMatchesDisplayed = practiceConsent?.terms_version === TERMS_VERSION
    && practiceConsent?.privacy_version === PRIVACY_VERSION;

  const acceptPracticeConsent = async () => {
    if (!practiceConsent || !policyMatchesDisplayed || !consentChecked || consentSaving) return;
    setConsentSaving(true);
    setConsentFeedback(null);
    try {
      await axios.post(`${BASE_URL}/api/account/practice-consent`, {
        terms_version: practiceConsent.terms_version,
        privacy_version: practiceConsent.privacy_version,
        accepted: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPracticeConsent((current) => ({ ...current, accepted: true }));
      setConsentChecked(false);
      setConsentFeedback({ type: 'success', message: 'Your current Terms and Privacy acceptance was recorded. Practice decisions still require all other eligibility checks.' });
    } catch (error) {
      setConsentFeedback({ type: 'error', message: error.response?.data?.detail || 'Acceptance could not be recorded. Review the current policies and try again.' });
    } finally {
      setConsentSaving(false);
    }
  };

  useEffect(() => {
    const pending2FA = localStorage.getItem('pending2FASetup');
    if (!pending2FA) return;
    setShowTwoFAPrompt(true);
    setNotification({
      type: 'warning',
      message: 'Two-factor authentication is not enabled yet. Complete the setup to secure your account.',
    });
    localStorage.removeItem('pending2FASetup');
  }, []);

  useEffect(() => {
    const result = searchParams.get('alpaca');
    if (!result) return;
    if (result === 'connected') {
      setConnectionFeedback({ type: 'success', message: 'Alpaca Paper connection completed. No broker order was created. Open Portfolio to review account access.' });
    } else if (result === 'cancelled') {
      setConnectionFeedback({ type: 'warning', message: 'Alpaca Paper connection was cancelled. Nothing changed. You can try Connect again.' });
    } else if (result === 'failed') {
      setConnectionFeedback({ type: 'error', message: 'Alpaca Paper could not complete the connection. Check the connection status and try again.' });
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchConnection = async () => {
      if (!token || !isTokenVerified) {
        navigate('/login');
        return;
      }
      setIsLoading(true);
      setConnectionStatusKnown(false);
      try {
        const isValid = await verifyAndRefreshToken();
        if (!isValid) {
          navigate('/login');
          return;
        }
        const headers = { Authorization: `Bearer ${token}` };
        const [connectionResponse, briefingResponse, tradeAlertsResponse, accountResponse] = await Promise.all([
          axios.get(`${BASE_URL}/api/alpaca/oauth/status`, { headers }),
          axios.get(`${BASE_URL}/api/briefing/preferences`, { headers }),
          axios.get(`${BASE_URL}/api/practice/trade-alert-preferences`, { headers }),
          axios.get(`${BASE_URL}/api/account`, { headers }),
        ]);
        setOauthStatus(connectionResponse.data);
        setConnectionStatusKnown(true);
        setBriefing(briefingResponse.data);
        setTradeAlerts(tradeAlertsResponse.data);
        setProfile(accountResponse.data);
      } catch {
        setConnectionStatusKnown(false);
        setConnectionFeedback({ type: 'error', message: 'Connection status is temporarily unavailable. Retry the status check; no connection was changed.' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchConnection();
  }, [token, isTokenVerified, verifyAndRefreshToken, navigate, connectionRefresh]);

  const startAlpacaConnect = async () => {
    setIsLoading(true);
    setConnectionFeedback({ type: 'info', message: 'Opening Alpaca Paper authorization…' });
    try {
      const response = await axios.post(
        `${BASE_URL}/api/alpaca/oauth/start?return_to=account`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.assign(response.data.authorization_url);
    } catch (error) {
      setConnectionFeedback({
        type: 'error',
        message: error.response?.data?.detail || 'Unable to start Alpaca Paper connection. Try Connect again.',
      });
      setIsLoading(false);
    }
  };

  const disconnectAlpaca = async () => {
    if (!window.confirm('Disconnect Alpaca from Pinngro? This removes Pinngro’s stored authorization but does not close your Alpaca account.')) return;
    setIsLoading(true);
    setConnectionFeedback({ type: 'info', message: 'Disconnecting Alpaca Paper from Pinngro…' });
    try {
      await axios.delete(`${BASE_URL}/api/alpaca/oauth/connection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOauthStatus((current) => ({ ...current, connected: false, connected_at: null, scopes: null }));
      setConnectionStatusKnown(true);
      setConnectionFeedback({ type: 'success', message: 'Alpaca Paper disconnected from Pinngro. Reconnect if you want Portfolio access again.' });
    } catch (error) {
      setConnectionFeedback({ type: 'error', message: error.response?.data?.detail || 'Unable to disconnect Alpaca Paper. Your connection was not changed; try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const saveBriefing = async () => {
    if (isSavingBriefing) return;
    setIsSavingBriefing(true);
    setBriefingFeedback(null);
    try {
      await axios.put(`${BASE_URL}/api/briefing/preferences`, {
        enabled: briefing.enabled,
        delivery_time: briefing.delivery_time,
        timezone: briefing.timezone,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBriefingFeedback({ type: 'success', message: 'Briefing settings saved.' });
    } catch {
      setBriefingFeedback({ type: 'error', message: "We couldn't save your briefing settings. Please try again." });
    } finally {
      setIsSavingBriefing(false);
    }
  };

  const saveProfile = async () => {
    if (profileSaveInFlight.current) return;
    profileSaveInFlight.current = true;
    setIsSavingProfile(true);
    setProfileFeedback(null);
    try {
      const response = await axios.patch(`${BASE_URL}/api/account/profile`, {
        country: profile.country,
        phone_number: profile.phone_number,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(response.data);
      setProfileFeedback({ type: 'success', message: 'Account profile saved.' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setProfileFeedback({ type: 'error', message: (typeof detail === 'string' ? detail : detail?.message) || 'Unable to save the account profile. Check your details and try again.' });
    } finally {
      profileSaveInFlight.current = false;
      setIsSavingProfile(false);
    }
  };

  const saveTradeAlerts = async () => {
    if (isSavingTradeAlerts) return;
    setIsSavingTradeAlerts(true);
    setTradeAlertFeedback(null);
    try {
      const response = await axios.put(
        `${BASE_URL}/api/practice/trade-alert-preferences`,
        {
          entry_alerts_enabled: tradeAlerts.entry_alerts_enabled,
          exit_alerts_enabled: tradeAlerts.exit_alerts_enabled,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTradeAlerts((current) => ({ ...current, ...response.data }));
      setTradeAlertFeedback({ type: 'success', message: 'Trade alert settings saved.' });
    } catch {
      setTradeAlertFeedback({ type: 'error', message: "We couldn't save your trade alert settings. Please try again." });
    } finally {
      setIsSavingTradeAlerts(false);
    }
  };

  const sendTestBriefing = async () => {
    if (isSendingTestEmail) return;
    setIsSendingTestEmail(true);
    setTestEmailFeedback(null);
    try {
      await axios.post(`${BASE_URL}/api/briefing/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestEmailFeedback({ type: 'success', message: 'Test email sent. Check your inbox.' });
    } catch {
      setTestEmailFeedback({ type: 'error', message: "We couldn't send the test email. Please try again." });
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const updateBriefingChoice = (change) => {
    setBriefing((current) => ({ ...current, ...change }));
    setBriefingFeedback(null);
  };

  const updateTradeAlertChoice = (change) => {
    setTradeAlerts((current) => ({ ...current, ...change }));
    setTradeAlertFeedback(null);
  };

  if (!isTokenVerified || !token) return <LoadingSpinner fullScreen showLabel label="Loading account settings…" />;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-24 sm:px-6 lg:px-8">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">Connections & security</p>
          <h1 className="mt-2 text-4xl font-black tracking-tight">Account settings</h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600 dark:text-slate-300">Connect Alpaca Paper through OAuth for Portfolio, market data and eligible manual Paper trading. Your password, account and funds remain with Alpaca.</p>
        </div>

        <div className="mt-8 space-y-6">
          {notification && <div role={notification.type === 'error' ? 'alert' : 'status'} aria-live="polite" className="rounded-xl border p-4" style={notificationStyles[notification.type]}>{notification.message}</div>}

          {showTwoFAPrompt && (
            <div className="flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-5 dark:border-amber-900 dark:bg-amber-950/30 sm:flex-row sm:items-center">
              <div className="flex-1"><p className="font-black">Protect your account with 2FA</p><p className="mt-1 text-sm text-amber-800 dark:text-amber-200">Two-factor authentication adds protection to your account and broker controls.</p></div>
              <button type="button" onClick={() => navigate('/enable-2fa')} className="rounded-xl bg-amber-600 px-5 py-3 font-bold text-white hover:bg-amber-700">Set up 2FA</button>
            </div>
          )}

          <section data-testid="account-profile" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-blue-600">Account profile</p>
            <h2 className="mt-2 text-2xl font-black">Your Pinngro account</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">Your sign-in name and verified email are shown for reference. Contact details can be updated without changing billing or broker authority.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-bold">Username<input value={profile.username || ''} readOnly className="mt-2 block w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-600 dark:border-slate-800 dark:bg-slate-950" /></label>
              <label className="text-sm font-bold">Verified email<input value={profile.email || ''} readOnly className="mt-2 block w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-600 dark:border-slate-800 dark:bg-slate-950" /></label>
              <label className="text-sm font-bold">Country<input value={profile.country || ''} onChange={(event) => { setProfile((current) => ({ ...current, country: event.target.value })); setProfileFeedback(null); }} className="mt-2 block w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 dark:border-slate-700" /></label>
              <label className="text-sm font-bold">Phone number<input value={profile.phone_number || ''} onChange={(event) => { setProfile((current) => ({ ...current, phone_number: event.target.value })); setProfileFeedback(null); }} placeholder="+356…" className="mt-2 block w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 dark:border-slate-700" /></label>
            </div>
            <button ref={profileSaveButtonRef} data-testid="save-account-profile" type="button" onClick={saveProfile} disabled={isLoading || isSavingProfile} aria-busy={isSavingProfile} className="mt-6 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50">{isSavingProfile ? 'Saving…' : 'Save account profile'}</button>
            {isSavingProfile && <p role="status" className="mt-2 text-sm text-blue-700 dark:text-blue-300">Saving your account profile…</p>}
            <ActionFeedback feedback={profileFeedback} testId="profile-save-feedback" />
            <p className="mt-4 text-xs text-slate-500">Account closure and erasure require a separately approved retention, active-subscription, and immutable-trading-evidence policy. No destructive self-service action is enabled yet.</p>
          </section>

          {practiceConsent?.legacy_eligible && (
            <section id="practice-consent" tabIndex="-1" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
              <h2 className="text-2xl font-black">Practice Terms and Privacy</h2>
              {!policyMatchesDisplayed ? (
                <p role="alert" className="mt-3 text-sm text-rose-700 dark:text-rose-300">The current policy documents are being updated. Acceptance is unavailable until they match the current versions.</p>
              ) : practiceConsent.accepted ? (
                <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">You have accepted the current Terms and Privacy versions for Practice decisions.</p>
              ) : (
                <>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Review the current <a className="font-bold underline" href="/terms" target="_blank" rel="noreferrer">Terms</a> and <a className="font-bold underline" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. Accepting them does not approve a trade or enable live trading.</p>
                  <label className="mt-5 flex items-start gap-3 text-sm font-medium"><input type="checkbox" checked={consentChecked} onChange={(event) => setConsentChecked(event.target.checked)} className="mt-1" />I have reviewed and accept the current Terms and Privacy Policy.</label>
                  <button type="button" onClick={acceptPracticeConsent} disabled={!policyMatchesDisplayed || !consentChecked || consentSaving} className="mt-4 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Record my acceptance</button>
                </>
              )}
              {consentFeedback && <p role={consentFeedback.type === 'error' ? 'alert' : 'status'} className="mt-4 text-sm">{consentFeedback.message}</p>}
            </section>
          )}

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-2xl ${!connectionStatusKnown ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' : oauthStatus.connected ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'}`}>{connectionStatusKnown && oauthStatus.connected ? <FaCheckCircle /> : <FaLock />}</span>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3"><h2 className="text-2xl font-black">Alpaca Paper connection</h2><span className={`rounded-full px-3 py-1 text-xs font-black ${connectionStatusKnown && oauthStatus.verified ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : !connectionStatusKnown ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>{!connectionStatusKnown ? 'STATUS UNAVAILABLE' : oauthStatus.verified ? 'READY' : oauthStatus.connected ? 'RECONNECT NEEDED' : 'NOT CONNECTED'}</span></div>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">Authorize on Alpaca’s website without giving Pinngro your password or pasting API keys. Eligible customers can place manual Alpaca Paper orders after review. Live and bot-driven broker trading remain disabled.</p>
                {connectionStatusKnown && oauthStatus.connected && oauthStatus.verified && <p className="mt-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">Alpaca Paper market-data connection ready</p>}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
                {!connectionStatusKnown ? <button type="button" disabled={isLoading} onClick={() => setConnectionRefresh((current) => current + 1)} className="rounded-xl bg-amber-600 px-5 py-3 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50">Retry connection status</button> : (
                  <>
                    {oauthStatus.connected && !oauthStatus.verified && <button type="button" disabled={isLoading || !oauthStatus.configured} onClick={startAlpacaConnect} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">Reconnect Alpaca</button>}
                    {oauthStatus.connected ? <button type="button" disabled={isLoading} onClick={disconnectAlpaca} className="rounded-xl border border-red-300 px-5 py-3 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30">Disconnect</button> : <button type="button" disabled={isLoading || !oauthStatus.configured} onClick={startAlpacaConnect} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 dark:disabled:bg-slate-700">{oauthStatus.configured ? 'Connect Alpaca for market data' : 'Connection unavailable'}</button>}
                  </>
                )}
                <a href="https://app.alpaca.markets/" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold text-slate-600 hover:text-blue-600 dark:text-slate-300">Open Alpaca <FaExternalLinkAlt /></a>
                {connectionFeedback && <p ref={connectionFeedbackRef} tabIndex={-1} role={connectionFeedback.type === 'error' ? 'alert' : 'status'} aria-live="polite" className="max-w-xs break-words text-sm leading-6 outline-none">{connectionFeedback.message}</p>}
              </div>
            </div>
            {connectionStatusKnown && !oauthStatus.configured && !oauthStatus.connected && <div className="mt-5 rounded-2xl bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-950/30 dark:text-blue-200"><strong>Alpaca Connect review is in progress.</strong> Secure OAuth connection will become available after approval.</div>}
            {connectionStatusKnown && oauthStatus.connected && !oauthStatus.verified && <div className="mt-5 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"><strong>Reconnect Alpaca for market data.</strong> Pinngro could not confirm the saved authorization. Your bots fail closed, and this check did not start a bot or create an order.</div>}
            {connectionStatusKnown && oauthStatus.connected && (
              <details className="mt-5 border-t border-slate-100 pt-4 text-xs text-slate-500 dark:border-slate-800">
                <summary className="cursor-pointer font-bold">Technical connection details</summary>
                <p className="mt-2">Environment: paper{oauthStatus.account_status ? ` · Account: ${oauthStatus.account_status}` : ''}{oauthStatus.scopes ? ` · OAuth permission: ${oauthStatus.scopes}` : ''}</p>
              </details>
            )}
          </section>

          <section className="grid gap-4 sm:grid-cols-3">
            <ConnectionPrinciple icon={<FaLock />} title="No password sharing">Sign in and approve access only on Alpaca’s website.</ConnectionPrinciple>
            <ConnectionPrinciple icon={<FaShieldAlt />} title="Non-custodial">Your account, positions, and funds remain with Alpaca.</ConnectionPrinciple>
            <ConnectionPrinciple icon={<FaCheckCircle />} title="Revocable access">Disconnect Pinngro and revoke authorization through Alpaca.</ConnectionPrinciple>
          </section>

          <section id="notifications" tabIndex="-1" data-testid="account-email-settings" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-blue-600">Notifications</p>
            <div id="morning-briefing" tabIndex="-1" className="mt-3 scroll-mt-28 outline-none"><div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"><div><h2 className="text-2xl font-black">Morning briefing</h2><p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">Receive one concise email with each bot’s latest Pinngro Practice Market Check. Disabled by default; unchanged results are skipped.</p></div><label className="flex items-center gap-3 font-bold"><input type="checkbox" checked={briefing.enabled} onChange={(event) => updateBriefingChoice({ enabled: event.target.checked })} className="h-5 w-5" /> Enabled</label></div></div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2"><label className="text-sm font-bold">Delivery time<input type="time" value={briefing.delivery_time} onChange={(event) => updateBriefingChoice({ delivery_time: event.target.value })} className="mt-2 block w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 dark:border-slate-700" /></label><label className="text-sm font-bold">Timezone<select value={briefing.timezone} onChange={(event) => updateBriefingChoice({ timezone: event.target.value })} className="mt-2 block w-full rounded-xl border border-slate-300 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"><option value="Europe/Malta">Europe/Malta</option><option value="UTC">UTC</option><option value="America/New_York">America/New_York</option><option value="America/Chicago">America/Chicago</option><option value="America/Los_Angeles">America/Los_Angeles</option><option value="Europe/London">Europe/London</option></select></label></div>
            {briefing.last_delivery && <p className="mt-4 text-xs text-slate-500">Last delivery: {briefing.last_delivery.status.replace('_', ' ')} · {new Date(briefing.last_delivery.created_at).toLocaleString()}</p>}
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div>
                <button ref={briefingSaveButtonRef} data-testid="save-briefing-settings" type="button" onClick={saveBriefing} disabled={isLoading || isSavingBriefing} aria-busy={isSavingBriefing} className="w-full rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"><span aria-live="polite">{isSavingBriefing ? 'Saving…' : 'Save briefing settings'}</span></button>
                <ActionFeedback feedback={briefingFeedback} testId="briefing-save-feedback" />
              </div>
              <div>
                <button ref={testEmailButtonRef} data-testid="send-test-email" type="button" onClick={sendTestBriefing} disabled={isLoading || isSendingTestEmail} aria-busy={isSendingTestEmail} className="w-full rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"><span aria-live="polite">{isSendingTestEmail ? 'Sending…' : 'Send test email'}</span></button>
                <ActionFeedback feedback={testEmailFeedback} testId="test-email-feedback" />
              </div>
            </div>

            <div className="mt-8 border-t border-slate-200 pt-7 dark:border-slate-700">
              <fieldset id="trade-suggestion-alerts" tabIndex="-1" className="scroll-mt-28 outline-none">
                <legend className="text-xl font-black">Trade suggestion alerts</legend>
                <p id="trade-alert-description" className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">Get an email when Pinngro creates a Practice proposal that needs your review. These alerts do not approve or place trades.</p>
                <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">These alerts cover Practice proposals only. Manual Alpaca Paper orders are separate; Live and automated broker trading remain disabled.</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2" aria-describedby="trade-alert-description">
                  <label className="flex min-h-16 cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                    <input type="checkbox" checked={tradeAlerts.entry_alerts_enabled} onChange={(event) => updateTradeAlertChoice({ entry_alerts_enabled: event.target.checked })} className="mt-1 h-5 w-5 shrink-0" />
                    <span><span className="block font-black">New entry suggestions</span><span className="mt-1 block text-sm leading-5 text-slate-600 dark:text-slate-300">Email me when Pinngro has a new Practice entry for me to review.</span></span>
                  </label>
                  <label className="flex min-h-16 cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                    <input type="checkbox" checked={tradeAlerts.exit_alerts_enabled} onChange={(event) => updateTradeAlertChoice({ exit_alerts_enabled: event.target.checked })} className="mt-1 h-5 w-5 shrink-0" />
                    <span><span className="block font-black">Exit &amp; cover suggestions</span><span className="mt-1 block text-sm leading-5 text-slate-600 dark:text-slate-300">Email me when an existing Practice position has an exit or cover requiring review.</span></span>
                  </label>
                </div>
                <button ref={tradeAlertSaveButtonRef} data-testid="save-trade-alert-settings" type="button" onClick={saveTradeAlerts} disabled={isSavingTradeAlerts} aria-busy={isSavingTradeAlerts} className="mt-5 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"><span aria-live="polite">{isSavingTradeAlerts ? 'Saving…' : 'Save trade alert settings'}</span></button>
                <ActionFeedback feedback={tradeAlertFeedback} testId="trade-alert-save-feedback" />
              </fieldset>
            </div>
            <PublicDisclosureSettings
              morningBriefingEnabled={briefing.enabled}
              onManageMorningBriefing={() => focusNotificationSection('morning-briefing')}
            />
          </section>

          <details className="mx-auto max-w-2xl text-center text-xs leading-5 text-slate-500">
            <summary className="cursor-pointer font-bold">About disconnecting</summary>
            <p className="mt-2">Disconnecting removes Pinngro’s stored OAuth authorization. It does not close your Alpaca account; revoke access in Alpaca as well when appropriate.</p>
          </details>
        </div>
      </main>
    </div>
  );
};

const ConnectionPrinciple = ({ icon, title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
    <span className="text-xl text-blue-600 dark:text-blue-400">{icon}</span>
    <h3 className="mt-3 font-black">{title}</h3>
    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{children}</p>
  </div>
);

const ActionFeedback = ({ feedback, testId }) => feedback && (
  <p
    data-testid={testId}
    role={feedback.type === 'error' ? 'alert' : 'status'}
    aria-live={feedback.type === 'error' ? 'assertive' : 'polite'}
    aria-atomic="true"
    className="mt-3 rounded-xl border p-3 text-sm font-bold"
    style={notificationStyles[feedback.type]}
  >
    {feedback.message}
  </p>
);

export default AccountSettings;
